import { InjectionToken, isDevMode, Provider, } from '@angular/core';
import { NavigationEnd, Router, } from '@angular/router';
import { AppStateFacade } from '@app-state';
import { AuthService } from '@services/auth.service';
import { BehaviorSubject, } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map, startWith, tap, } from 'rxjs/operators';
import { convertToCategory, PageCategory, PageMetadata, PageScope, SIDEBAR_SECTION_METADATA, SIDEBAR_SECTION_TYPES, } from './page-metadata';
export var ORCHESTRATOR_PAGE_META = new InjectionToken('Orchestrator Page Metadata');
export function providePageMeta(page) {
    return {
        provide: ORCHESTRATOR_PAGE_META,
        useValue: page,
        multi: true,
    };
}
var DEFAULT_PAGE = new PageMetadata({
    category: NaN,
    title: '',
    translationKey: '',
    routerLink: '',
    scope: PageScope.Global,
});
var priorityComparer = function (a, b) { return a.priority - b.priority; };
var ɵ0 = priorityComparer;
var mapPageMeta = function (page, _idx, pages) {
    var parent = pages.find(function (p) { return p.routerLink === page.parentRoute; });
    if (!parent && !!page.parentRoute) {
        throw new Error("Could not find parent '" + page.parentRoute + "' for page: '" + page.title + "'");
    }
    return new PageMetadata(page, parent);
};
var ɵ1 = mapPageMeta;
var cleanUrl = function (url) { return url.split('?').shift(); };
var ɵ2 = cleanUrl;
var PageMetadataRegistry = /** @class */ (function () {
    function PageMetadataRegistry(pages, _appState, _router, 
    // FIXME: remove after identity server is implemented
    _auth) {
        var _this = this;
        this._appState = _appState;
        this._router = _router;
        this._auth = _auth;
        this._pageMap = new Map();
        this._pages = [];
        this._activePage$ = new BehaviorSubject(DEFAULT_PAGE);
        this.activePage$ = this._activePage$
            .asObservable();
        this._pages = pages
            .map(mapPageMeta);
        this._pages
            .forEach(function (page) {
            if (!_this._pageMap.has(page.category)) {
                _this._pageMap.set(page.category, []);
            }
            _this._pageMap.get(page.category).push(page);
        });
        this.activePage$.subscribe(function (page) {
            _this._appState.user.folderPermissions.flushCache();
            if (isDevMode()) {
                console.log('Page Registry Active Page: ', page);
            }
        });
    }
    Object.defineProperty(PageMetadataRegistry.prototype, "activePage", {
        get: function () {
            return this._activePage$.value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "commonPages", {
        get: function () {
            return this._pageMap.get(PageCategory.All);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "sidebarSections", {
        get: function () {
            var sidebarPages = this._enabledUserPages
                .filter(function (page) { return page.type === 'Sidebar'; });
            if (this._appState.user.loggedInAsHost) {
                return [{ pages: sidebarPages.sort(priorityComparer) }];
            }
            return SIDEBAR_SECTION_TYPES.map(function (section) { return ({
                name: section,
                metadata: SIDEBAR_SECTION_METADATA[section],
                pages: sidebarPages.filter(function (p) { return p.sidebarSection === section; }).sort(priorityComparer),
            }); });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "userMenuPages", {
        get: function () {
            return this._enabledUserPages
                .filter(function (page) { return page.type === 'Admin'; })
                .sort(priorityComparer);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "cleanCurrentUrl", {
        get: function () {
            return cleanUrl(this._router.url);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "pages", {
        get: function () {
            return this._pages;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "_enabledUserPages", {
        get: function () {
            var _this = this;
            return this._userPages.filter(function (page) {
                return !page.featureFlag ||
                    _this._appState.settings.isFeatureEnabled(page.featureFlag);
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PageMetadataRegistry.prototype, "_userPages", {
        get: function () {
            var state = this._auth.pendingLogout$.value
                ? PageCategory.Unauthorized :
                convertToCategory(this._appState.user.authState);
            return this._pageMap.get(state);
        },
        enumerable: true,
        configurable: true
    });
    PageMetadataRegistry.prototype.listen = function () {
        var _this = this;
        this._router
            .events
            .pipe(filter(function (ev) {
            return ev instanceof NavigationEnd ||
                // restricted access page will not generate NavigationEnd, but NavigationCancel
                _this._router.url.startsWith('/restricted-access');
        }), startWith(void 0), map(function () { return _this.pageForCurrentRoute(); }), map(function (page) {
            if (!page) {
                throw new Error("No metadata was registered for '" + _this._router.url + "'.");
            }
            return page;
        }), distinctUntilKeyChanged('routerLink'), tap(function () { return _this.setCustomTitle(''); })).subscribe(function (page) { return _this._activePage$.next(page); });
    };
    PageMetadataRegistry.prototype.find = function (id) {
        return this.pages.find(function (p) { return p.identifier === id; });
    };
    PageMetadataRegistry.prototype.setCustomTitle = function (value) {
        this.activePage.customTitle = value;
    };
    PageMetadataRegistry.prototype.findByTitle = function (title) {
        return this.pages.find(function (p) { return p.title === title; });
    };
    PageMetadataRegistry.prototype.isActivePage = function (_a) {
        var cleanRouterLink = _a.cleanRouterLink;
        return this.activePage$.pipe(map(function (activePage) {
            return activePage && cleanRouterLink === activePage.cleanRouterLink;
        }), distinctUntilChanged());
    };
    PageMetadataRegistry.prototype.hasActivePage = function (_a) {
        var cleanRouterLink = _a.cleanRouterLink;
        return this.activePage$.pipe(map(function (activePage) {
            return activePage &&
                (cleanRouterLink === activePage.cleanRouterLink ||
                    activePage.parent &&
                        activePage.parent.cleanRouterLink === cleanRouterLink);
        }), distinctUntilChanged());
    };
    PageMetadataRegistry.prototype.findByRoute = function (link, searchAllTargets) {
        if (searchAllTargets === void 0) { searchAllTargets = false; }
        var cleanLink = cleanUrl(link);
        var result = this._userPages.find(function (page) { return page.isMatch(cleanLink); });
        if (!result && searchAllTargets) {
            result = this._pages.find(function (page) { return page.isMatch(cleanLink); });
        }
        if (!result) {
            result = this._pageMap.get(PageCategory.All)
                .find(function (page) { return page.isMatch(cleanLink); });
        }
        return result;
    };
    PageMetadataRegistry.prototype.pageForCurrentRoute = function () {
        return this.findByRoute(this.cleanCurrentUrl, this._appState.authConfig.value.auth.isCustomSsoEnabled);
    };
    return PageMetadataRegistry;
}());
export { PageMetadataRegistry };
export { ɵ0, ɵ1, ɵ2 };
